import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCountries, fetchCitiesByCountry, getFighters } from "../../../services/api";
import { FighterOption } from "../../../types/types";

export const CountryDropdown: React.FC<{
  value: string;
  onChange: (val: string) => void;
  isInvalid?: boolean;
}> = ({ value, onChange, isInvalid }) => {
  const { t } = useTranslation("CreateEditOpportunities");
  const [countries, setCountries] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState(value ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  useEffect(() => {
    fetchCountries()
      .then(setCountries)
      .catch((err) => console.error("Failed to load countries", err));
  }, []);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = inputValue.trim()
    ? countries.filter((country) => country.toLowerCase().includes(inputValue.toLowerCase()))
    : countries;

  const handleSelect = (event: React.MouseEvent, country: string) => {
    event.preventDefault();
    setInputValue(country);
    onChange(country);
    setIsOpen(false);
  };

  const openDropdown = () => {
    setIsOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };
  const borderClass = isInvalid
    ? "border-red-500 ring-1 ring-red-200"
    : isOpen
      ? "border-[#0080FF] ring-2 ring-[#0080FF]/20"
      : "border-[#E4E4E7]";

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        onMouseDown={(event) => {
          if ((event.target as HTMLElement).closest("button")) return;
          openDropdown();
        }}
        className={`flex items-center w-full h-10 border rounded-2xl px-3.5 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition ${borderClass}`}
      >
        <svg
          className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mr-1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" />
          <path
            strokeLinecap="round"
            d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={t("searchCountry")}
          onChange={(event) => {
            setInputValue(event.target.value);
            onChange(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="flex-1 text-xs text-gray-800 bg-transparent outline-none placeholder-gray-400 min-w-0"
        />
        {inputValue && (
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              setInputValue("");
              onChange("");
              setIsOpen(true);
            }}
            className="text-gray-300 hover:text-gray-500 transition flex-shrink-0 mr-0.5"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            openDropdown();
          }}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-gray-400 transition hover:text-gray-600"
          aria-label={t("openCountryList")}
        >
          <svg
            className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""
              }`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {isOpen && (
        <ul className="absolute z-50 mt-1.5 w-full max-h-52 overflow-y-auto bg-white border border-[#E4E4E7] rounded-2xl shadow-lg py-1.5">
          {filtered.length > 0 ? (
            filtered.map((country) => (
              <li
                key={country}
                onMouseDown={(event) => handleSelect(event, country)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC] ${country === inputValue
                    ? "bg-[#EBEBEC] text-gray-900 font-semibold"
                    : "text-gray-900"
                  }`}
              >
                {country === inputValue && (
                  <svg
                    className="w-3 h-3 text-blue-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                )}
                <span className={country === inputValue ? "" : "ml-5"}>{country}</span>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-xs text-gray-400 text-center">
              {t("noCountriesFound")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export const CityDropdown: React.FC<{
  value: string;
  country: string;
  onChange: (val: string) => void;
  isInvalid?: boolean;
}> = ({ value, country, onChange, isInvalid }) => {
  const { t } = useTranslation("CreateEditOpportunities");
  const [cities, setCities] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState(value ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevCountryRef = useRef<string>("");

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!country) {
      setCities([]);
      if (prevCountryRef.current) {
        setInputValue("");
        onChange("");
      }
      prevCountryRef.current = "";
      return;
    }
    if (country === prevCountryRef.current) return;
    if (prevCountryRef.current && prevCountryRef.current !== country) {
      setInputValue("");
      onChange("");
    }
    prevCountryRef.current = country;
    setIsLoading(true);
    setCities([]);
    fetchCitiesByCountry(country)
      .then(setCities)
      .catch((err) => console.error("Failed to load cities", err))
      .finally(() => setIsLoading(false));
  }, [country, onChange]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = inputValue.trim()
    ? cities.filter((city) => city.toLowerCase().includes(inputValue.toLowerCase()))
    : cities;

  const isDisabled = !country;

  const openDropdown = () => {
    if (isDisabled || isLoading) return;
    setIsOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };
  const borderClass = isDisabled
    ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
    : isInvalid
      ? "border-red-500 ring-1 ring-red-200"
      : isOpen
        ? "border-[#0080FF] ring-2 ring-[#0080FF]/20"
        : "border-[#E4E4E7]";

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        onMouseDown={(event) => {
          if ((event.target as HTMLElement).closest("button")) return;
          openDropdown();
        }}
        className={`flex items-center w-full h-10 border rounded-2xl px-3.5 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition ${borderClass}`}
      >
        <svg
          className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mr-1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 21h18M3 7l9-4 9 4M4 21V7m16 14V7M9 21V11h6v10"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={isLoading ? "" : inputValue}
          placeholder={
            isDisabled
              ? t("selectCountryFirst")
              : isLoading
                ? t("loadingCities")
                : t("searchCity")
          }
          onChange={(event) => {
            setInputValue(event.target.value);
            onChange(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!isDisabled && !isLoading) setIsOpen(true);
          }}
          disabled={isDisabled || isLoading}
          className="flex-1 text-xs text-gray-800 bg-transparent outline-none placeholder-gray-400 min-w-0 disabled:cursor-not-allowed"
        />
        {isLoading && (
          <svg
            className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
            />
          </svg>
        )}
        {!isLoading && inputValue && !isDisabled && (
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              setInputValue("");
              onChange("");
              setIsOpen(true);
            }}
            className="text-gray-300 hover:text-gray-500 transition flex-shrink-0 mr-0.5"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {!isLoading && (
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              openDropdown();
            }}
            disabled={isDisabled}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-gray-400 transition hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("openCityList")}
          >
            <svg
              className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      {isOpen && !isDisabled && !isLoading && (
        <ul className="absolute z-50 mt-1.5 w-full max-h-52 overflow-y-auto bg-white border border-[#E4E4E7] rounded-2xl shadow-lg py-1.5">
          {filtered.length > 0 ? (
            filtered.map((city) => (
              <li
                key={city}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setInputValue(city);
                  onChange(city);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC] ${city === inputValue
                    ? "bg-[#EBEBEC] text-gray-900 font-semibold"
                    : "text-gray-900"
                  }`}
              >
                {city === inputValue && (
                  <svg
                    className="w-3 h-3 text-blue-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                )}
                <span className={city === inputValue ? "" : "ml-5"}>{city}</span>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-xs text-gray-400 text-center">
              {t("noCitiesFound")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export const MultiSelectDropdown: React.FC<{
  value: string[];
  options: { label: string; value: string }[];
  placeholder?: string;
  onChange: (val: string[]) => void;
  isInvalid?: boolean;
}> = ({ value = [], options, placeholder, onChange, isInvalid }) => {
  const { t } = useTranslation("CreateEditOpportunities");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (event: React.MouseEvent, optValue: string) => {
    event.preventDefault();
    const current = Array.isArray(value) ? value : [];
    onChange(
      current.includes(optValue)
        ? current.filter((optionValue) => optionValue !== optValue)
        : [...current, optValue],
    );
  };

  const removeTag = (event: React.MouseEvent, optValue: string) => {
    event.stopPropagation();
    onChange(
      (Array.isArray(value) ? value : []).filter(
        (optionValue) => optionValue !== optValue,
      ),
    );
  };

  const selectedLabels = (Array.isArray(value) ? value : []).map(
    (optionValue) =>
      options.find((option) => option.value === optionValue)?.label ??
      optionValue,
  );
  const borderClass = isInvalid
    ? "border-red-500 ring-1 ring-red-200"
    : isOpen
      ? "border-[#0080FF] ring-2 ring-[#0080FF]/20"
      : "border-[#E4E4E7]";

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
        className={`min-h-[40px] flex items-center flex-wrap gap-1 w-full border rounded-2xl px-3.5 py-1 bg-white cursor-pointer shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition ${borderClass}`}
      >
        {selectedLabels.length > 0 ? (
          selectedLabels.map((label, index) => (
            <span
              key={index}
              className="flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 text-[10px] font-medium"
            >
              {label}
              <button
                type="button"
                onMouseDown={(event) =>
                  removeTag(
                    event,
                    (Array.isArray(value) ? value : [])[index],
                  )
                }
                className="text-blue-400 hover:text-blue-600 transition"
              >
                <svg
                  className="w-2.5 h-2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-400">{placeholder || t("selectOptions")}</span>
        )}
        <svg
          className={`w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && options.length > 0 && (
        <ul className="absolute z-50 mt-1.5 w-full max-h-52 overflow-y-auto bg-white border border-[#E4E4E7] rounded-2xl shadow-lg py-1.5">
          {options.map((opt) => {
            const selected = (Array.isArray(value) ? value : []).includes(opt.value);
            return (
              <li
                key={opt.value}
                onMouseDown={(event) => toggle(event, opt.value)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC] ${selected ? "bg-[#EBEBEC] text-gray-900 font-semibold" : "text-gray-900"
                  }`}
              >
                <span
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                    }`}
                >
                  {selected && (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  )}
                </span>
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
      {isOpen && options.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-xs text-gray-400 text-center">
          {t("noOptionsAvailable")}
        </div>
      )}
    </div>
  );
};

export const DurationInput: React.FC<{
  value: number | string;
  unit?: string;
  min?: number;
  max?: number;
  onChange: (val: number | "") => void;
  isInvalid?: boolean;
}> = ({ value, unit = "minutes", min, max, onChange, isInvalid }) => {
  const { t } = useTranslation("CreateEditOpportunities");
  const unitLabel =
    unit === "minutes"
      ? t("minutes")
      : unit === "seconds"
        ? t("seconds")
        : unit === "hours"
          ? t("hours")
          : unit;
  const borderClass = isInvalid
    ? "border-red-500 ring-1 ring-red-200 focus:border-red-500 focus:ring-red-100"
    : "border-[#E4E4E7] focus:border-[#0080FF] focus:ring-[#0080FF]/20";

  return (
    <div className="relative flex-1">
      <input
        type="number"
        className={`w-full h-10 border rounded-2xl pl-3.5 pr-10 text-sm text-[#11181C] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus:ring-2 ${borderClass} transition placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
        placeholder={t("enterField", { field: unitLabel })}
        value={value ?? ""}
        min={min}
        max={max}
        onChange={(event) =>
          onChange(
            event.target.value === "" ? "" : Number(event.target.value),
          )
        }
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-400 pointer-events-none select-none">
        {unitLabel}
      </span>
    </div>
  );
};



export const FighterSearchInput: React.FC<{
  value: string | number;
  displayName?: string;
  displayField?: string;
  valueField?: string;
  allowManualEntry?: boolean;
  storeSelectedAs?: "id" | "name";
  placeholder?: string;
  onChange: (fighterId: string | number, fighterName: string) => void;
  isInvalid?: boolean;
}> = ({
  value,
  displayName = "",
  displayField = "fighterName",
  valueField = "fighterId",
  allowManualEntry = false,
  storeSelectedAs = "id",
  placeholder = "",
  onChange,
  isInvalid,
}) => {
    const { t } = useTranslation("CreateEditOpportunities");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<FighterOption[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedName, setSelectedName] = useState(displayName || "");
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      setSelectedName(displayName || "");
    }, [displayName, value]);

    useEffect(() => {
      const handler = (event: MouseEvent) => {
        if (!containerRef.current?.contains(event.target as Node)) {
          if (allowManualEntry && query.trim()) {
            handleManualEntry(query);
          }
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [allowManualEntry, query]);

    useEffect(() => {
      if (!query.trim()) {
        loadFighters("");
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        loadFighters(query);
      }, 300);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [query]);

    const loadFighters = async (searchText: string) => {
      setIsLoading(true);
      try {
        const response = await getFighters({
          name: searchText || undefined,
          page: 0,
          pageSize: 200,
        });
        const list = (response.data || []).map((rawFighter: {
          id?: string | number;
          profileId?: string | number;
          fighter?: {
            id?: string | number;
            firstName?: string;
            lastName?: string;
            status?: string;
            profileImage?: string;
          } | null;
          fullName?: string;
          name?: string;
          firstName?: string;
          lastName?: string;
          status?: string;
          profileImage?: string;
        }) => ({
          fighterId: rawFighter.fighter?.id || rawFighter.profileId || rawFighter.id,
          fighterName: rawFighter.fighter
            ? `${rawFighter.fighter.firstName || ""} ${rawFighter.fighter.lastName || ""}`.trim()
            : rawFighter.fullName || rawFighter.name || `${rawFighter.firstName || ""} ${rawFighter.lastName || ""}`.trim(),
          status: (rawFighter.fighter?.status || rawFighter.status || "").toUpperCase(),
          profileImage: rawFighter.profileImage || rawFighter.fighter?.profileImage || null,
        }));
        setResults(list);
        setIsOpen(true);
      } catch (err) {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const handleSelect = (fighter: FighterOption) => {
      setSelectedName(fighter.fighterName);
      setQuery("");
      setIsOpen(false);
      onChange(
        storeSelectedAs === "name" ? fighter.fighterName : fighter.fighterId,
        fighter.fighterName,
      );
    };

    const handleManualEntry = (manualName: string) => {
      const nextName = manualName.trim();
      setSelectedName(nextName);
      setQuery("");
      setIsOpen(false);
      onChange(storeSelectedAs === "name" ? nextName : "", nextName);
    };

    const handleClear = (event: React.MouseEvent) => {
      event.preventDefault();
      setSelectedName("");
      setQuery("");
      setResults([]);
      onChange("", "");
    };

    const displayValue =
      selectedName ||
      (storeSelectedAs === "name" && value ? String(value) : "");
    const canUseManual =
      allowManualEntry &&
      query.trim() &&
      !results.some(
        (fighter) =>
          fighter.fighterName.trim().toLowerCase() === query.trim().toLowerCase(),
      );
    const borderClass = isInvalid
      ? "border-red-500 ring-1 ring-red-200"
      : isOpen
        ? "border-[#0080FF] ring-2 ring-[#0080FF]/20"
        : "border-[#E4E4E7]";

    return (
      <div ref={containerRef} className="relative w-full">
        <div
          className={`flex items-center w-full h-10 border rounded-2xl px-3.5 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition ${borderClass}`}
        >
          <svg
            className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mr-1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
          </svg>

          {displayValue && !query ? (
            <span className="flex-1 flex items-center gap-1.5 min-w-0">
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 text-[10px] font-semibold truncate max-w-[180px]">
                <svg
                  className="w-2.5 h-2.5 text-blue-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                {displayValue}
              </span>
              <button
                type="button"
                onMouseDown={handleClear}
                className="text-gray-300 hover:text-red-400 transition ml-1 flex-shrink-0"
                title={t("clearSelection")}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ) : (
            <input
              type="text"
              value={query}
              placeholder={displayValue || placeholder || t("searchFighterByName")}
              onChange={(event) => {
                const nextQuery = event.target.value;
                setQuery(nextQuery);
                if (allowManualEntry) {
                  onChange(
                    storeSelectedAs === "name" ? nextQuery : "",
                    nextQuery,
                  );
                }
              }}
              onKeyDown={(event) => {
                if (
                  allowManualEntry &&
                  event.key === "Enter" &&
                  query.trim()
                ) {
                  event.preventDefault();
                  handleManualEntry(query);
                }
              }}
              onFocus={() => {
                setIsOpen(true);
              }}
              className="flex-1 text-xs text-gray-800 bg-transparent outline-none placeholder-gray-400 min-w-0"
            />
          )}

          {isLoading && (
            <svg
              className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
              />
            </svg>
          )}
        </div>

        {isOpen && (
          <ul className="absolute z-50 mt-1.5 w-full max-h-52 overflow-y-auto bg-white border border-[#E4E4E7] rounded-2xl shadow-lg py-1.5">
            {results.length > 0 || canUseManual ? (
              <>
                {canUseManual && (
                  <li
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleManualEntry(query);
                    }}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer rounded-xl hover:bg-[#EBEBEC] transition"
                  >
                    <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                      </svg>
                    </div>
                    <span className="truncate">
                      {t("useManualValue", { value: query.trim() })}
                    </span>
                  </li>
                )}
                {results.map((fighter) => (
                  <li
                    key={String(fighter.fighterId)}
                    onMouseDown={() => handleSelect(fighter)}
                    className="flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer rounded-xl hover:bg-[#EBEBEC] transition"
                  >
                    {fighter.profileImage ? (
                      <img
                        src={fighter.profileImage}
                        alt={fighter.fighterName}
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-gray-200"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                        </svg>
                      </div>
                    )}
                    <span className="truncate">{fighter.fighterName}</span>
                  </li>
                ))}
              </>
            ) : (
              <li className="px-3 py-2.5 text-xs text-gray-400 text-center">
                {query.trim()
                  ? t("noFightersFoundForSearch")
                  : allowManualEntry
                    ? t("searchOrTypeCustomFighter")
                    : t("typeToSearchFighters")}
              </li>
            )}
          </ul>
        )}
      </div>
    );
  };
